import { useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { EASE } from "./Reveal";

export const Lightbox = ({ items, index, onClose, onChange, interval = 5000 }) => {
  const open = index !== null;
  const [playing, setPlaying] = useState(true);
  const prev = useCallback(() => onChange((index - 1 + items.length) % items.length), [index, items.length, onChange]);
  const next = useCallback(() => onChange((index + 1) % items.length), [index, items.length, onChange]);

  useEffect(() => {
    if (!open || !playing) return;
    const id = setTimeout(next, interval);
    return () => clearTimeout(id);
  }, [open, playing, index, next, interval]);

  useEffect(() => {
    if (open) setPlaying(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    document.documentElement.classList.add("lenis-stopped");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.documentElement.classList.remove("lenis-stopped");
    };
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0a0a0b]/92 backdrop-blur-2xl"
          onClick={onClose}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Bezárás"
            className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/15"
          >
            <X size={18} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Előző kép"
            className="absolute left-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/15 sm:left-8"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Következő kép"
            className="absolute right-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/15 sm:right-8"
          >
            <ChevronRight size={20} />
          </button>

          <div className="relative mx-16 flex max-h-[85vh] w-full max-w-6xl flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <AnimatePresence mode="wait">
              <motion.div
                key={items[index].src}
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="relative max-h-[75vh] overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
              >
                <motion.img
                  src={items[index].src}
                  alt={items[index].alt}
                  data-testid="lightbox-image"
                  initial={{ scale: 1 }}
                  animate={{ scale: playing ? 1.08 : 1 }}
                  transition={{ duration: interval / 1000 + 1, ease: "linear" }}
                  className="max-h-[75vh] w-auto max-w-full object-contain"
                />
                {playing && (
                  <motion.span
                    key={`bar-${index}`}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: interval / 1000, ease: "linear" }}
                    className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-[var(--glow)]"
                    data-testid="lightbox-progress"
                  />
                )}
              </motion.div>
            </AnimatePresence>
            <div className="flex items-center gap-4 font-mono text-xs text-white/50">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                data-testid="lightbox-play-toggle"
                aria-label={playing ? "Diavetítés szüneteltetése" : "Diavetítés indítása"}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/15"
              >
                {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
              </button>
              <span data-testid="lightbox-counter">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
              <span className="h-px w-8 bg-white/20" />
              <span className="text-white/80">{items[index].alt}</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
